import { Injectable } from '@angular/core';
import { Participant } from '@models/Sessions/Participant';
import { Session } from '@models/Sessions/Session';
import { Team } from '@models/Sessions/Team';
import { startWith } from 'rxjs/operators';

import { BusService } from '../bus.service';
import { EventsService } from '../events.service';
import { RequestService } from '../request.service';

type IGetSessionResponse = Session;

@Injectable()
export class SessionsRequestService {
  api = {
    root: '/session',
    adminAll: '/all',
    updateEmailNotifSettings: 'update-notif-email-settings',
    follow: '/follow',
    unfollow: '/unfollow',
    config: '/config',
    participant: '/participants',
    team: '/teams',
    report: '/report',
    export: '/export',
    import: '/import',
  };

  private cachedResponses = {};

  isCached(route) {
    return route in this.cachedResponses;
  }

  getCache(route) {
    return this.cachedResponses[route];
  }

  updateCache(route, response) {
    this.cachedResponses[route] = response;
  }

  constructor(
    private client: RequestService,
    private bus: BusService,
    private events: EventsService,
  ) {
    this.subscribe();
  }

  getAll() {
    const requestObservable = this.client.get<IGetSessionResponse[]>({
      uri: this.api.root,
      handlers: {
        success: response => {
          this.updateCache('getAll', response);
          this.bus.publish(
            this.events.received.data.session.all.success,
            response,
          );
        },
        error: error =>
          this.bus.publish(
            this.events.received.data.session.all.failure,
            error.error,
          ),
      },
    });

    if (this.isCached('getAll')) {
      // first return cached response, then return fetched response from server
      return requestObservable.pipe(startWith(this.getCache('getAll')));
    } else {
      return requestObservable;
    }
  }

  getAdminAll() {
    return this.client.get<IGetSessionResponse[]>({
      uri: this.api.root + '/' + this.api.adminAll,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.data.session.adminAll.success,
            response,
          ),
        error: error =>
          this.bus.publish(
            this.events.received.data.session.adminAll.failure,
            error.error,
          ),
      },
    });
  }

  updateEmailNotifSettings(event) {
    this.client.post<any, any>({
      uri: this.api.root + '/' + this.api.updateEmailNotifSettings,
      body: {
        ids: event.ids,
        key: event.key,
        value: event.value,
      },
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.data.session.updateEmailNotifSettings.success,
            response,
          ),
        error: error =>
          this.bus.publish(
            this.events.received.data.session.updateEmailNotifSettings.failure,
            error.error,
          ),
      },
    });
  }

  getSingle(id: string) {
    return this.client.get<IGetSessionResponse>({
      uri: this.api.root + `/${id}`,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.data.session.single.success,
            response,
          ),
        error: error =>
          this.bus.publish(
            this.events.received.data.session.single.failure,
            error.error,
          ),
      },
    });
  }

  getReport(id: string) {
    return this.client.get<any>({
      uri: this.api.root + `/${id}` + this.api.report,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.data.session.report.success,
            response,
          ),
        error: error =>
          this.bus.publish(
            this.events.received.data.session.report.failure,
            error.error,
          ),
      },
    });
  }

  getExport(id: string) {
    this.client.get<any>({
      uri: this.api.root + `/${id}` + this.api.export,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.data.session.export.success,
            response,
          ),
        error: error =>
          this.bus.publish(
            this.events.received.data.session.export.failure,
            error.error,
          ),
      },
    });
  }

  create(session: Session) {
    this.client.post<any, any>({
      uri: this.api.root,
      body: session,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.create.success,
            response,
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.create.failure,
            error.error,
          ),
      },
    });
  }

  import(data, session: Session) {
    this.client.post<any, any>({
      uri: this.api.root + `/${session._id}` + this.api.import,
      body: data,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.import.success,
            response,
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.import.failure,
            error.error,
          ),
      },
    });
  }

  updateConfig(session: Session) {
    this.client.put<any, any>({
      uri: this.api.root + `/${session._id}` + this.api.config,
      body: session.config,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.updateConfig.success,
            response,
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.updateConfig.failure,
            error.error,
          ),
      },
    });
  }

  follow(session: Session) {
    return this.client.post<any, any>({
      uri: this.api.root + `/${session._id}` + this.api.follow,
      body: {},
      handlers: {
        success: response =>
          this.bus.publish(this.events.received.action.session.follow.success, {
            response,
            session,
          }),
        error: error =>
          this.bus.publish(this.events.received.action.session.follow.failure, {
            error: error.error,
            session,
          }),
      },
    });
  }

  unfollow(session: Session) {
    return this.client.post<any, any>({
      uri: this.api.root + `/${session._id}` + this.api.unfollow,
      body: {},
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.unfollow.success,
            { response, session },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.unfollow.failure,
            { error: error.error, session },
          ),
      },
    });
  }

  // Participants

  addParticipant(participant: Participant, session: Session) {
    this.client.post<any, any>({
      uri: this.api.root + `/${session._id}` + this.api.participant,
      body: participant,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.participants.add.success,
            { response, session },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.participants.add.failure,
            { error: error.error, session },
          ),
      },
    });
  }

  updateParticipant(participant: Participant, session: Session) {
    this.client.put<any, any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.participant +
        `/${participant._id}`,
      body: participant,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.participants.update.success,
            { response, session },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.participants.update.failure,
            { error: error.error, session },
          ),
      },
    });
  }

  deleteParticipant(participant: Participant, session: Session) {
    this.client.delete<any>({
      uri:
        this.api.root +
        `/${session._id}` +
        this.api.participant +
        `/${participant._id}`,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.participants.delete.success,
            { response, session },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.participants.delete.failure,
            { error: error.error, session },
          ),
      },
    });
  }

  // Teams

  addTeam(team: Team, session: Session) {
    this.client.post<any, any>({
      uri: this.api.root + `/${session._id}` + this.api.team,
      body: team,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.session.teams.add.success,
            { response, session },
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.session.teams.add.failure,
            { error: error.error, session },
          ),
      },
    });
  }

  private subscribe() {
    this.bus.subscribe(
      this.events.requested.data.session.adminAll,
      this.getAdminAll.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.data.session.all,
      this.getAll.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.data.session.updateEmailNotifSettings,
      this.updateEmailNotifSettings.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.data.session.single,
      this.getSingle.bind(this),
    );

    this.bus.subscribe(
      this.events.requested.data.session.report,
      this.getReport.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.data.session.export,
      this.getExport.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.follow,
      this.follow.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.unfollow,
      this.unfollow.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.create,
      this.create.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.import,
      this.import.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.updateConfig,
      this.updateConfig.bind(this),
    );
    // Particpants
    this.bus.subscribe(
      this.events.requested.action.session.participants.add,
      this.addParticipant.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.participants.update,
      this.updateParticipant.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.session.participants.delete,
      this.deleteParticipant.bind(this),
    );
    // Teams
    this.bus.subscribe(
      this.events.requested.action.session.teams.add,
      this.addTeam.bind(this),
    );
  }
}
